/**
 * AssetModal Component
 *
 * Unified wizard modal for adding AND editing asset nodes in attack paths.
 * Steps:
 * 1. AssetSelectionStep - Search/select Chariot asset or add custom
 * 2. AssetIconStep - Select icon for the asset
 *
 * Features:
 * - Supports both 'add' and 'edit' modes via mode prop
 * - useOptimistic for instant node creation/update feedback
 * - Suspense boundaries for declarative loading
 * - ARIA live regions for accessibility
 * - Input sanitization for security
 */

import {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useOptimistic,
  useState,
} from 'react';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';

import { Modal } from '@/components/Modal';
import type { WizardConfig, WizardStep } from '@/components/wizards';
import {
  useWizard,
  useWizardFooterConfig,
  WizardModalFooter,
  WizardProgressBar,
  WizardStepContainer,
} from '@/components/wizards';
import { useAttackState } from '@/sections/attacks/attackPaths/context';
import { useNodeUpdate } from '@/sections/attacks/attackPaths/hooks';
import type { CustomAssetNode } from '@/sections/attacks/attackPaths/types';

import { StepLoadingSkeleton } from './components';
import { AssetIconStep, AssetSelectionStep } from './steps';
import type { AssetWizardFormData } from './types';
import {
  assetFormDataFromNode,
  createAssetFormData,
  validateAssetStep,
  validateIconStep,
} from './types';

/**
 * Modal mode type
 */
type NodeModalMode = 'add' | 'edit';

/**
 * Props for AssetModal component
 */
interface AssetModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** Modal mode: 'add' for new nodes, 'edit' for existing nodes */
  mode: NodeModalMode;
  /** For edit mode: existing node data to pre-populate */
  initialNode?: CustomAssetNode;
  /** Optional callback when node is successfully created/updated */
  onComplete?: () => void;
}

/**
 * Wizard step definitions for asset creation/editing
 */
const assetWizardSteps: WizardStep<AssetWizardFormData>[] = [
  {
    id: 'asset-selection',
    order: 1,
    title: 'Select Asset',
    description: 'Choose or create an asset to add to the attack path',
    validate: validateAssetStep,
  },
  {
    id: 'icon-selection',
    order: 2,
    title: 'Choose Icon',
    description: 'Select an icon for this asset',
    validate: validateIconStep,
    isFinal: true,
  },
];

/**
 * Creates a CustomAssetNode from wizard form data.
 */
function createAssetNode(formData: AssetWizardFormData): CustomAssetNode {
  return {
    id: uuidv4(),
    type: 'asset',
    position: { x: 0, y: 0 }, // Position will be set by layout
    data: {
      id: formData.assetId,
      type: formData.isCustom ? 'nonChariot' : undefined,
      iconOverride: formData.iconOverride,
      displayName: formData.assetDisplayName,
      metadata: formData.assetMetadata,
    },
  };
}

/**
 * Mode-specific labels for UI elements
 */
const MODE_LABELS = {
  add: {
    title: 'Add Asset Node',
    submit: 'Add',
    submitting: 'Adding...',
    successMessage: 'Asset node added to attack path',
    errorMessage: 'Failed to add asset node',
  },
  edit: {
    title: 'Edit Asset Node',
    submit: 'Save',
    submitting: 'Saving...',
    successMessage: 'Asset node updated',
    errorMessage: 'Failed to update asset node',
  },
} as const;

/**
 * AssetModal - Unified modal wizard for adding/editing asset nodes.
 *
 * Implements a 2-step flow:
 * 1. Asset selection (search existing or add custom)
 * 2. Icon customization (optional)
 *
 * Uses React 19 patterns:
 * - useOptimistic for instant node addition/update with automatic rollback
 * - Suspense for declarative loading states
 *
 * @example
 * ```tsx
 * // Add mode
 * <AssetModal
 *   open={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   mode="add"
 *   onComplete={() => toast.success('Asset added')}
 * />
 *
 * // Edit mode
 * <AssetModal
 *   open={editModalOpen}
 *   onClose={() => setEditModalOpen(false)}
 *   mode="edit"
 *   initialNode={existingNode}
 *   onComplete={() => toast.success('Asset updated')}
 * />
 * ```
 */
export function AssetModal({
  open,
  onClose,
  mode,
  initialNode,
  onComplete,
}: AssetModalProps) {
  const { nodes, addNode } = useAttackState();
  const { updateAsset } = useNodeUpdate(initialNode?.id ?? '');

  // Force fresh IconPicker instance on each modal open (prevents stale selection state)
  const [modalKey, setModalKey] = useState(0);

  const labels = MODE_LABELS[mode];

  // Compute initial data based on mode
  const initialData = useMemo(() => {
    if (mode === 'edit' && initialNode) {
      return assetFormDataFromNode(initialNode);
    }
    return createAssetFormData();
  }, [mode, initialNode]);

  // Optimistic state for instant UI feedback
  const [, addOptimisticNode] = useOptimistic(
    nodes,
    (state, newNode: CustomAssetNode) => [...state, newNode]
  );

  /**
   * Handle wizard submission - create or update the node
   */
  const handleSubmit = useCallback(
    async (formData: AssetWizardFormData) => {
      try {
        if (mode === 'edit' && initialNode) {
          // Edit mode: update existing node
          updateAsset(
            formData.assetId,
            formData.isCustom,
            formData.iconOverride,
            formData.assetDisplayName,
            formData.assetMetadata
          );
          toast.success(labels.successMessage);
        } else {
          // Add mode: create new node
          const newNode = createAssetNode(formData);
          addOptimisticNode(newNode);
          addNode(newNode);
          toast.success(labels.successMessage);
        }
        onComplete?.();
        onClose();
      } catch {
        toast.error(labels.errorMessage);
      }
    },
    [
      mode,
      initialNode,
      updateAsset,
      addOptimisticNode,
      addNode,
      labels,
      onClose,
      onComplete,
    ]
  );

  // Configure wizard
  const wizardConfig: WizardConfig<AssetWizardFormData> = useMemo(
    () => ({
      steps: assetWizardSteps,
      initialData,
      onSubmit: handleSubmit,
    }),
    [initialData, handleSubmit]
  );

  const wizard = useWizard(wizardConfig);
  const { reset } = wizard;

  // Reset wizard to step 1 when modal opens
  // This ensures clean state after any close scenario (submit, cancel, escape, click outside)
  useEffect(() => {
    if (open) {
      reset();
      // Increment key to force fresh IconPicker instance (clears stale selection state)
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Intentional: Force remount on modal open
      setModalKey(prev => prev + 1);
    }
  }, [open, reset]);

  // Configure footer buttons
  const footerConfig = useWizardFooterConfig({
    canGoNext: wizard.navigation.canGoNext,
    canGoBack: wizard.navigation.canGoBack,
    isPending: wizard.isSubmitting,
    isLastStep: wizard.navigation.isLastStep,
    onNext: wizard.navigation.handleNext,
    onBack: wizard.navigation.handleBack,
    onCancel: () => {
      wizard.reset();
      onClose();
    },
    onSubmit: wizard.submit,
    labels: {
      next: 'Next',
      back: 'Back',
      cancel: 'Cancel',
      submit: labels.submit,
      submitting: labels.submitting,
    },
  });

  /**
   * Handle modal close with reset
   */
  const handleClose = () => {
    wizard.reset();
    onClose();
  };

  /**
   * Render the current step content
   */
  const renderStepContent = () => {
    switch (wizard.currentStepId) {
      case 'asset-selection':
        return (
          <AssetSelectionStep
            description={
              mode === 'edit'
                ? 'Change the asset or update to a custom one.'
                : 'Search for an existing Chariot asset or add a custom one to include in the attack path.'
            }
            formData={{
              assetId: wizard.formData.assetId,
              assetDisplayName: wizard.formData.assetDisplayName,
              assetMetadata: wizard.formData.assetMetadata,
              isCustom: wizard.formData.isCustom,
            }}
            onAssetChange={(assetId, isCustom, displayName, metadata) =>
              wizard.setFormData(prev => ({
                ...prev,
                assetId,
                isCustom,
                assetDisplayName: displayName,
                assetMetadata: metadata,
              }))
            }
            // Pass preselectedKey in edit mode to ensure the selected asset is in the list for auto-scroll
            preselectedKey={
              mode === 'edit' && !initialData.isCustom
                ? initialData.assetId
                : undefined
            }
          />
        );
      case 'icon-selection':
        return (
          <AssetIconStep
            key={modalKey}
            formData={wizard.formData}
            setFormData={wizard.setFormData}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={labels.title}
      hideFooter
      size="4xl"
      data-testid={`${mode}-asset-wizard-modal`}
    >
      {/* ARIA live region for step changes */}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        Step {wizard.progress.currentDisplayStep} of{' '}
        {wizard.progress.totalDisplaySteps}: {wizard.progress.currentStepTitle}
      </div>

      {/* Progress bar */}
      <WizardProgressBar
        currentStep={wizard.progress.currentDisplayStep}
        totalSteps={wizard.progress.totalDisplaySteps}
        stepTitle={wizard.progress.currentStepTitle}
        percentage={wizard.progress.percentage}
      />

      {/* Step content with Suspense boundary */}
      <WizardStepContainer>
        <Suspense fallback={<StepLoadingSkeleton />}>
          {renderStepContent()}
        </Suspense>
      </WizardStepContainer>

      {/* Error display */}
      {wizard.submitError && (
        <div
          className="rounded-md bg-red-500/10 p-3 text-sm text-red-500"
          role="alert"
        >
          {wizard.submitError}
        </div>
      )}

      {/* Footer with navigation buttons */}
      <WizardModalFooter config={footerConfig} className="mt-0" />
    </Modal>
  );
}
